import React from 'react';

import { useFormikContext } from 'formik';

import { useManagementPackContext } from '../ManagementPackContext';

import SECTION_OPTIONS from './sectionOptions';

import EmptyState from 'CommonComponents/EmptyState';
import ToggleInput from 'CommonComponents/ToggleInput';

import ListIcon from 'Assets/icons/list.svg';

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * A section's saved options over its defaults, one level deep, so an option
 * group saved before a new option existed still shows that option's default.
 */
const withDefaults = (defaults, saved) => {
    const base = defaults || {};
    const given = saved || {};

    return Object.keys({ ...base, ...given }).reduce(
        (merged, key) => ({
            ...merged,
            [key]: isPlainObject(base[key]) && isPlainObject(given[key]) ? { ...base[key], ...given[key] } : (given[key] ?? base[key]),
        }),
        {},
    );
};

/**
 * The pack's sections, in their fixed order, each switched on or off. An
 * enabled section with options of its own shows them underneath.
 */
const SectionsTab = () => {
    const { values, setFieldValue } = useFormikContext();
    const { options, readOnly } = useManagementPackContext();

    const sections = options.sections || [];

    if (!sections.length) {
        return (
            <div className="tw-py-10">
                <EmptyState
                    icon={ListIcon}
                    heading={$t('No sections yet')}
                    body={$t('Section pages will appear here as they are released. The pack currently has its cover, contents and basis of preparation.')}
                />
            </div>
        );
    }

    const scopeCostcentreIds = values.scope?.org === false ? new Set(values.scope.costcentreIds || []) : null;
    const scopedCostcentres = (options.costcentres || []).filter((costcentre) => !scopeCostcentreIds || scopeCostcentreIds.has(costcentre.value));
    const scopedDepartmentIds = new Set(scopedCostcentres.map((costcentre) => costcentre.departmentId));
    // A cost-centre pack covers its cost centres' plans and their departments' plans only.
    const scopedPlans = (options.plans || []).filter(
        (plan) => !scopeCostcentreIds || scopeCostcentreIds.has(plan.costcentreId) || (plan.isDepartment && scopedDepartmentIds.has(plan.departmentId)),
    );

    const configured = new Map(values.sections.map((section) => [section.key, section]));

    const update = (key, changes) => {
        setFieldValue(
            'sections',
            sections.map((section) => {
                const current = configured.get(section.key) || {
                    key: section.key,
                    enabled: section.defaultEnabled ?? true,
                    options: section.defaultOptions || {},
                };

                return section.key === key ? { ...current, ...changes(current) } : current;
            }),
        );
    };

    const toggle = (key) => update(key, (current) => ({ enabled: !current.enabled }));

    return (
        <ol className="tw-list-none tw-m-0 tw-p-0">
            {sections.map((section) => {
                const enabled = configured.get(section.key)?.enabled ?? section.defaultEnabled ?? true;
                const Options = SECTION_OPTIONS[section.key];

                return (
                    <li key={section.key} className="tw-py-4 tw-border-b tw-border-charcoal-15 last:tw-border-b-0">
                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
                            <div className="tw-flex tw-flex-col">
                                {section.tag && <span className="tw-text-2xs tw-font-semibold tw-uppercase tw-text-blue">{section.tag}</span>}
                                <span className="tw-text-sm tw-font-semibold tw-text-charcoal">{section.title}</span>
                            </div>
                            <ToggleInput
                                label={<span className="tw-sr-only">{section.title}</span>}
                                noBottomSpacing
                                checked={enabled}
                                disabled={readOnly}
                                onChange={() => toggle(section.key)}
                            />
                        </div>
                        {Options && (enabled || Options.showWhenDisabled) && (
                            <Options
                                enabled={enabled}
                                options={withDefaults(section.defaultOptions, configured.get(section.key)?.options)}
                                costcentres={scopedCostcentres}
                                departments={options.departments || []}
                                plans={scopedPlans}
                                currencyCode={options.currencyCode}
                                cashflowTaxAvailable={!!options.cashflowTaxAvailable}
                                readOnly={readOnly}
                                onChange={(sectionOptions) => update(section.key, () => ({ options: sectionOptions }))}
                            />
                        )}
                    </li>
                );
            })}
        </ol>
    );
};

export default SectionsTab;
